import { useMemo, useCallback, useEffect, useRef, type RefObject } from "react"
import { useTranslation } from 'react-i18next'
import { FlashCard } from "@/components/shared/tattoo/FlashCard"
import { TattooCard } from "@/components/shared/tattoo/TattooCard"
import { BaseMasonryGrid } from "@/components/shared/BaseMasonryGrid"
import { useQueryTattoos } from "@/hooks/useQueryTattoos"
import { useQueryTattoosByFyp } from "@/hooks/useQueryTattoosByFyp"
import { useSearchTattooFeed } from "@/hooks/useSearchTattooFeed"
import { useDynamicData } from "@/hooks/useDynamicData"
import { useArtists } from "@/hooks/useArtists"
import { getUniqueArtists } from "@/lib/tattooData"
import type { PortfolioItem, Artist } from "@/types/models"
import type { VectorSearchResult } from "@/lib/vectorSearchApi"
import type { SortingOption } from "./tattoo/filters/SortingFilter"
import {
  nearMatchExpansionForQueryCities,
  resolveSelectedLocationsToQueryCities,
} from "@/lib/locationRegions"
import { markDiscoverLoadPhase } from "@/lib/discoverPerf"

interface GridProps {
  selectedCollections: ("flashes" | "portfolioItems" | "healedPortfolioItems")[]
  selectedStyle: string
  selectedArtist: string
  selectedLocations: string[]
  sorting: SortingOption
  includeBooked?: boolean
  showDeals?: boolean
  searchResults?: VectorSearchResult[]
  isSearching?: boolean
  searchError?: string | null
  // Edit mode props
  editMode?: boolean
  onDelete?: (itemId: string) => void
  /** Hide these item ids until the feed no longer contains them (avoids delete flicker while refetching) */
  excludeItemIds?: Set<string>
  /** Fired when the loaded feed item ids change; use to clear exclude entries after refetch */
  onFeedItemIdsChange?: (itemIds: string[]) => void
  onMetadataChange?: (itemId: string, updates: { minPrice?: string; minSize?: string; booked?: boolean }) => void
  onMetadataSave?: (itemId: string, updates?: { minPrice?: string; minSize?: string; booked?: boolean }) => Promise<void>
  onRefreshFlashes?: () => void
  // Profile page mode - shows empty state even with just artist filter, no fallback
  isProfilePage?: boolean
  // Selection mode props
  selectionMode?: boolean
  selectedItems?: string[]
  onItemToggle?: (item: PortfolioItem) => void
  // Hide artist info from cards
  hideArtistInfo?: boolean
  // Maximum number of items that can be selected
  maxSelection?: number
  // Nested context flag - prevents scrolling when Grid is used in a modal/selector
  isNestedContext?: boolean
  // Debug: show skeleton as semi-transparent overlay so grid activity is visible underneath
  debugSkeleton?: boolean
  // Detail modal callback
  onDetailClick?: (itemId: string, itemType: 'flash' | 'tattoo' | 'healed') => void
  // Hide completely when empty (no empty state shown)
  hideWhenEmpty?: boolean
  // Hide save pill on flash cards
  hideSavePill?: boolean
  /** Cache key for feed state persistence across navigation */
  cacheKey?: string
  /** When incremented with the same filters, re-fetches without remounting the grid (e.g. artist profile after delete) */
  refetchNonce?: number
  /** When incremented after uploads, forces a full in-hook reload (pairs with awaited cache invalidation) */
  uploadRevision?: number
  /** If set, that item is moved to the start of the grid (e.g. deep-linked flash in chat picker) */
  leadWithItemId?: string | null
  /** Auto-load batches until this id is in loaded items (see useQueryTattoos prefetch). */
  prefetchUntilItemId?: string | null
  /** Scroll container for infinite scroll when the grid is not window-scrolled (e.g. chat picker). */
  scrollRootRef?: RefObject<HTMLElement | null>
  /** Prepend these items if their ids are missing from the query (e.g. deep-linked flash from Firestore). */
  injectItemsIfMissing?: PortfolioItem[]
  /** When true, location filter is “all markets” (incl. every launched city). Overrides URL-only heuristics for card flags. */
  locationFilterCoversAllMarkets?: boolean
  /** Primary feed masonry only: skeleton overlay visible or fading (not near-match grid). */
  onPrimarySkeletonOverlayActiveChange?: (active: boolean) => void
}

export function Grid({
  selectedCollections,
  selectedStyle = "All Styles",
  selectedArtist = "All Artists", 
  selectedLocations = [],
  sorting = 'trending',
  includeBooked = false,
  showDeals = false,
  searchResults,
  isSearching = false,
  searchError = null,
  editMode = false,
  onDelete,
  excludeItemIds,
  onFeedItemIdsChange,
  onMetadataChange,
  onMetadataSave,
  onRefreshFlashes,
  isProfilePage = false,
  selectionMode = false,
  selectedItems = [],
  onItemToggle,
  hideArtistInfo = false,
  isNestedContext = false,
  debugSkeleton = false,
  onDetailClick,
  hideWhenEmpty = false,
  hideSavePill = false,
  cacheKey,
  refetchNonce,
  uploadRevision,
  leadWithItemId = null,
  prefetchUntilItemId = null,
  scrollRootRef,
  injectItemsIfMissing,
  locationFilterCoversAllMarkets,
  onPrimarySkeletonOverlayActiveChange,
}: GridProps) {
  const { t } = useTranslation()
  const { data } = useDynamicData()
  const feedType =
    selectedCollections.includes('flashes') && selectedCollections.includes('portfolioItems')
      ? 'mixed'
      : selectedCollections.includes('flashes')
      ? 'flashes'
      : 'tattoos'
  
  // Track previous values to detect meaningful changes
  const prevValuesRef = useRef({
    collections: selectedCollections,
    style: selectedStyle,
    artist: selectedArtist,
    locations: selectedLocations,
    sorting: sorting,
    includeBooked: includeBooked,
    showDeals: showDeals,
    searchResultsKey: '',
    isSearching: isSearching
  })
  
  // Create a stable identifier for search results to properly track changes
  const searchResultsKey = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return 'no-results'
    return searchResults.map(r => r.id).join(',')
  }, [searchResults])
  
  // Scroll to top when filters, search, or collections change (after initial mount)
  useEffect(() => {
    const prev = prevValuesRef.current
    
    // Check if any value actually changed
    const hasChanged = 
      JSON.stringify(prev.collections) !== JSON.stringify(selectedCollections) ||
      prev.style !== selectedStyle ||
      prev.artist !== selectedArtist ||
      JSON.stringify(prev.locations) !== JSON.stringify(selectedLocations) ||
      prev.sorting !== sorting ||
      prev.includeBooked !== includeBooked ||
      prev.showDeals !== showDeals ||
      prev.searchResultsKey !== searchResultsKey ||
      (prev.isSearching && !isSearching) // Transition from searching to not searching
    
    // Update refs
    prevValuesRef.current = {
      collections: selectedCollections,
      style: selectedStyle,
      artist: selectedArtist,
      locations: selectedLocations,
      sorting: sorting,
      showDeals: showDeals,
      includeBooked: includeBooked,
      searchResultsKey: searchResultsKey,
      isSearching: isSearching
    }
    
    // Don't scroll to top if we're in edit mode (to preserve scroll position while editing)
    // Also don't scroll if we're in a nested context (like ArtistContentSelector modal)
    // Only scroll if something changed and we're not on initial mount
    if (hasChanged && !editMode && !isNestedContext) {
      // Use a small delay to ensure content is rendered
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 50)
    }
  }, [
    selectedCollections,
    selectedStyle,
    selectedArtist,
    selectedLocations,
    sorting,
    includeBooked,
    showDeals,
    searchResultsKey,
    isSearching,
    editMode,
    isNestedContext
  ])
  
  // Memoize all computed values
  const translatedTexts = useMemo(() => ({
    allStylesText: t('onskin.filters.allStyles'),
    allArtistsText: t('onskin.filters.allArtists'),
    allLocationsText: t('onskin.filters.allLocations')
  }), [t])

  const locationIsGlobal =
    locationFilterCoversAllMarkets === true ||
    selectedLocations.length === 0 ||
    selectedLocations.includes(translatedTexts.allLocationsText)

  // Show when browsing all markets (incl. full multi-select) and not profile/event
  const shouldShowCountryFlags = locationIsGlobal && !isProfilePage

  // Never strip cities just because the selection matches “all launched” in the UI (e.g. one launched
  // city + user picked that city). Backend treats missing `cities` as no geo filter (global artists).
  const resolvedCitiesForQuery = useMemo(() => {
    if (selectedLocations.length === 0 || selectedLocations.includes(translatedTexts.allLocationsText)) {
      return undefined
    }
    const list = resolveSelectedLocationsToQueryCities(
      selectedLocations,
      translatedTexts.allLocationsText
    )
    return list?.length ? list : undefined
  }, [selectedLocations, translatedTexts.allLocationsText])

  const nearMatchExpansion = useMemo(
    () => nearMatchExpansionForQueryCities(resolvedCitiesForQuery),
    [resolvedCitiesForQuery],
  )

  const styleMapping = useMemo(() => {
    const selectedStyleData = data?.filterTattooStyle?.find(s => 
      s.styleName === selectedStyle
    )
    return {
      styleIdToMatch: selectedStyleData?.styleId
    }
  }, [data, selectedStyle])

  // Build filters for the backend query
  const filters = useMemo(() => {
    // Create filters even if data isn't loaded yet, since we can still query without style filters
    // We only need the data if we're filtering by a specific style
    const needsData = selectedStyle !== translatedTexts.allStylesText
    if (needsData && !data) {
      console.log("[Grid] Waiting for dynamic data to build filters (style filter needed)")
      return undefined
    }

    return {
      collections: selectedCollections,
      sorting: sorting,
      cities: resolvedCitiesForQuery,
      artistId: selectedArtist !== translatedTexts.allArtistsText ? selectedArtist : undefined,
      style: selectedStyle === translatedTexts.allStylesText ? undefined : styleMapping.styleIdToMatch,
      includeBooked,
      deals: showDeals ? true : false,
      ...(refetchNonce !== undefined ? { clientRefetchNonce: refetchNonce } : {}),
      ...(uploadRevision !== undefined ? { clientUploadRevision: uploadRevision } : {}),
    }
  }, [
    data,
    selectedCollections,
    selectedStyle,
    selectedArtist,
    selectedLocations,
    resolvedCitiesForQuery,
    sorting,
    includeBooked,
    showDeals,
    refetchNonce,
    uploadRevision,
    styleMapping.styleIdToMatch,
    translatedTexts
  ])

  // Build filters for FYP query (no sorting parameter)
  const fypFilters = useMemo(() => {
    // Create filters even if data isn't loaded yet, since we can still query without style filters
    const needsData = selectedStyle !== translatedTexts.allStylesText
    if (needsData && !data) {
      console.log("[Grid] Waiting for dynamic data to build FYP filters (style filter needed)")
      return undefined
    }

    return {
      collections: selectedCollections,
      cities: resolvedCitiesForQuery,
      artistId: selectedArtist !== translatedTexts.allArtistsText ? selectedArtist : undefined,
      style: selectedStyle === translatedTexts.allStylesText ? undefined : styleMapping.styleIdToMatch,
      includeBooked,
      deals: showDeals ? true : false,
    }
  }, [
    data,
    selectedCollections,
    selectedStyle,
    selectedArtist,
    selectedLocations,
    resolvedCitiesForQuery,
    includeBooked,
    showDeals,
    styleMapping.styleIdToMatch,
    translatedTexts
  ])

  /** Same as primary query but all catalog cities in the user’s country (when a narrower city list was empty). */
  const nearMatchFilters = useMemo(() => {
    if (!filters || !nearMatchExpansion) return undefined
    const widened = { ...filters, cities: nearMatchExpansion.cities }
    if (widened.sorting === "fyp") {
      return { ...widened, sorting: "trending" as const satisfies SortingOption }
    }
    return widened
  }, [filters, nearMatchExpansion])

  // Determine which feed to use
  const hasSearchResults = searchResults !== undefined && searchResults.length > 0 && !searchError
  const isInSearchMode = hasSearchResults || isSearching
  const isInFypMode = sorting === 'fyp' && !isInSearchMode
  
  // Only run the feed that we actually need
  const hasActiveFilters = useMemo(() => {
    return selectedArtist !== translatedTexts.allArtistsText || 
           selectedStyle !== translatedTexts.allStylesText ||
           !locationIsGlobal
  }, [selectedArtist, selectedStyle, locationIsGlobal, translatedTexts])

  // Check if there are meaningful filters (style or location) that should trigger empty state
  // Artist-only filters should not show empty state since they have fallback behavior
  // UNLESS we're on a profile page, where we want to show empty state for the artist
  const hasMeaningfulFilters = useMemo(() => {
    return selectedStyle !== translatedTexts.allStylesText ||
           !locationIsGlobal ||
           (isProfilePage && selectedArtist !== translatedTexts.allArtistsText)
  }, [selectedStyle, locationIsGlobal, selectedArtist, translatedTexts, isProfilePage])

  // Only run the feed that we actually need
  const regularFeed = useQueryTattoos(
    !isInFypMode ? filters : undefined,
    cacheKey,
    { prefetchUntilItemId: prefetchUntilItemId ?? null }
  )
  const fypFeed = useQueryTattoosByFyp(isInFypMode ? fypFilters : undefined, cacheKey)
  const searchFeed = useSearchTattooFeed({ 
    searchResults: isInSearchMode ? (searchResults || []) : [], 
    isSearching: isInSearchMode ? isSearching : false,
    searchError: isInSearchMode ? searchError : null,
    collections: selectedCollections
  })

  const { items, loading, loadingMore, hasMore, error, loadMoreItems, initialLoadComplete, searchScores } = 
    isInSearchMode ? searchFeed : isInFypMode ? fypFeed : regularFeed

  const processedItems = items

  const gridLoadSummaryLoggedRef = useRef(false)
  useEffect(() => {
    if (gridLoadSummaryLoggedRef.current) return
    if (!initialLoadComplete || loading) return
    if (isInSearchMode || isInFypMode) return
    gridLoadSummaryLoggedRef.current = true
    markDiscoverLoadPhase("grid_data_ready", {
      itemCount: processedItems.length,
      sorting,
      cacheKey,
    })
  }, [
    initialLoadComplete,
    loading,
    isInSearchMode,
    isInFypMode,
    processedItems.length,
    sorting,
    cacheKey,
  ])

  /**
   * Run widened (e.g. country) query as soon as the primary feed is settled and empty.
   * Previously this used `nearMatchUnlocked` state updated in an effect — one frame late —
   * which let `showEmptyState` / near-match intro flash before `emptyStateWaitsOnNearMatch` kicked in.
   */
  const shouldRunNearMatchQuery =
    !isInSearchMode &&
    !isProfilePage &&
    initialLoadComplete &&
    !loading &&
    processedItems.length === 0 &&
    hasActiveFilters &&
    nearMatchExpansion != null &&
    nearMatchFilters !== undefined

  const nearMatchFeed = useQueryTattoos(
    shouldRunNearMatchQuery ? nearMatchFilters : undefined,
    undefined,
    { prefetchUntilItemId: prefetchUntilItemId ?? null }
  )

  const itemsToDisplay = processedItems

  const itemsWithInjected = useMemo(() => {
    if (!injectItemsIfMissing?.length) return itemsToDisplay
    const ids = new Set(itemsToDisplay.map((i) => i.id))
    const extras = injectItemsIfMissing.filter((i) => i?.id && !ids.has(i.id))
    return extras.length ? [...extras, ...itemsToDisplay] : itemsToDisplay
  }, [itemsToDisplay, injectItemsIfMissing])

  const feedItemIds = useMemo(() => processedItems.map((i) => i.id), [processedItems])

  useEffect(() => {
    if (!onFeedItemIdsChange) return
    onFeedItemIdsChange(feedItemIds)
  }, [feedItemIds, onFeedItemIdsChange])

  const itemsForGrid = useMemo(() => {
    let list = !excludeItemIds?.size
      ? itemsWithInjected
      : itemsWithInjected.filter((i) => !excludeItemIds.has(i.id))
    if (leadWithItemId) {
      const idx = list.findIndex((i) => i.id === leadWithItemId)
      if (idx > 0) {
        const pinned = list[idx]
        list = [pinned, ...list.slice(0, idx), ...list.slice(idx + 1)]
      }
    }
    return list
  }, [itemsWithInjected, excludeItemIds, leadWithItemId])

  const nearItemsWithInjected = useMemo(() => {
    const raw = nearMatchFeed.items
    if (!injectItemsIfMissing?.length) return raw
    const ids = new Set(raw.map((i) => i.id))
    const extras = injectItemsIfMissing.filter((i) => i?.id && !ids.has(i.id))
    return extras.length ? [...extras, ...raw] : raw
  }, [nearMatchFeed.items, injectItemsIfMissing])

  const nearItemsForGrid = useMemo(() => {
    let list = !excludeItemIds?.size
      ? nearItemsWithInjected
      : nearItemsWithInjected.filter((i) => !excludeItemIds.has(i.id))
    if (leadWithItemId) {
      const idx = list.findIndex((i) => i.id === leadWithItemId)
      if (idx > 0) {
        const pinned = list[idx]
        list = [pinned, ...list.slice(0, idx), ...list.slice(idx + 1)]
      }
    }
    return list
  }, [nearItemsWithInjected, excludeItemIds, leadWithItemId])

  // Incremental masonry assumes append-only ordering; pinning reorders — remount when pin reaches slot 0.
  const pinAtTopReady = !leadWithItemId || itemsForGrid[0]?.id === leadWithItemId
  const masonryLayoutKey = leadWithItemId
    ? `grid-pin-${leadWithItemId}-${pinAtTopReady ? 't' : 'p'}`
    : 'grid-default'

  const uniqueArtistIds = useMemo(() => getUniqueArtists(itemsForGrid), [itemsForGrid])
  const { artists, loading: artistsLoading } = useArtists(uniqueArtistIds)

  const uniqueNearArtistIds = useMemo(() => getUniqueArtists(nearItemsForGrid), [nearItemsForGrid])
  const { artists: nearArtists, loading: nearArtistsLoading } = useArtists(uniqueNearArtistIds)
  
  // Track milestones
  const hasStartedInitialLoadRef = useRef(false)

  // True only on the very first render if we already have data from cache — used to skip
  // the loading skeleton so the feed appears instantly when navigating back
  const mountedWithCachedDataRef = useRef(initialLoadComplete && items.length > 0)
  
  // Track when we start an initial load to prevent showing empty state during first query
  useEffect(() => {
    if (!hasStartedInitialLoadRef.current && (filters || fypFilters)) {
      hasStartedInitialLoadRef.current = true
    }
  }, [filters, fypFilters])

  const renderPrimaryCard = useCallback((item: PortfolioItem, artist?: Artist, editMode?: boolean, onDelete?: (itemId: string) => void) => {
    const isSelected = selectedItems.includes(item.id)
    const canShowSelectionIndicator = selectionMode
    
    if (item.isFlash) {
      return (
        <FlashCard 
          item={item} 
          artist={hideArtistInfo ? undefined : artist}
          searchScore={searchScores?.get(item.id)}
          editMode={editMode}
          onDelete={onDelete}
          onMetadataChange={onMetadataChange}
          onMetadataSave={onMetadataSave}
          showCountryFlag={shouldShowCountryFlags}
          onRefreshFlashes={onRefreshFlashes}
          feedType={feedType}
          selectionMode={selectionMode}
          isSelected={isSelected}
          canShowSelectionIndicator={canShowSelectionIndicator}
          onToggleSelect={onItemToggle ? () => onItemToggle(item) : undefined}
          onDetailClick={onDetailClick ? () => onDetailClick(item.id, 'flash') : undefined}
          hideSavePill={hideSavePill}
        />
      )
    }
    return (
      <TattooCard 
        item={item} 
        artist={hideArtistInfo ? undefined : artist}
        artistsLoading={artistsLoading}
        searchScore={searchScores?.get(item.id)}
        editMode={editMode}
        onDelete={onDelete}
        showCountryFlag={shouldShowCountryFlags}
        feedType={feedType}
        selectionMode={selectionMode}
        isSelected={isSelected}
        canShowSelectionIndicator={canShowSelectionIndicator}
        onToggleSelect={onItemToggle ? () => onItemToggle(item) : undefined}
        onDetailClick={onDetailClick ? () => onDetailClick(item.id, item.isHealed ? 'healed' : 'tattoo') : undefined}
        hideShare
      />
    )
  }, [searchScores, artistsLoading, onMetadataChange, onMetadataSave, shouldShowCountryFlags, selectionMode, selectedItems, onItemToggle, hideArtistInfo, onDetailClick, feedType, onRefreshFlashes, hideSavePill])

  const renderNearMatchCard = useCallback((item: PortfolioItem, artist?: Artist, editMode?: boolean, onDelete?: (itemId: string) => void) => {
    const isSelected = selectedItems.includes(item.id)
    const canShowSelectionIndicator = selectionMode
    
    if (item.isFlash) {
      return (
        <FlashCard 
          item={item} 
          artist={hideArtistInfo ? undefined : artist}
          searchScore={searchScores?.get(item.id)}
          editMode={editMode}
          onDelete={onDelete}
          onMetadataChange={onMetadataChange}
          onMetadataSave={onMetadataSave}
          showCountryFlag={true}
          onRefreshFlashes={onRefreshFlashes}
          feedType={feedType}
          selectionMode={selectionMode}
          isSelected={isSelected}
          canShowSelectionIndicator={canShowSelectionIndicator}
          onToggleSelect={onItemToggle ? () => onItemToggle(item) : undefined}
          onDetailClick={onDetailClick ? () => onDetailClick(item.id, 'flash') : undefined}
          hideSavePill={hideSavePill}
        />
      )
    }
    return (
      <TattooCard 
        item={item} 
        artist={hideArtistInfo ? undefined : artist}
        artistsLoading={nearArtistsLoading}
        searchScore={searchScores?.get(item.id)}
        editMode={editMode}
        onDelete={onDelete}
        showCountryFlag={true}
        feedType={feedType}
        selectionMode={selectionMode}
        isSelected={isSelected}
        canShowSelectionIndicator={canShowSelectionIndicator}
        onToggleSelect={onItemToggle ? () => onItemToggle(item) : undefined}
        onDetailClick={onDetailClick ? () => onDetailClick(item.id, item.isHealed ? 'healed' : 'tattoo') : undefined}
        hideShare
      />
    )
  }, [searchScores, nearArtistsLoading, onMetadataChange, onMetadataSave, selectionMode, selectedItems, onItemToggle, hideArtistInfo, onDetailClick, feedType, onRefreshFlashes, hideSavePill])

  /** Search mode, or filter object ready so the active tattoos hook can run (FYP uses fypFilters). */
  const primaryQueryPayloadReady =
    isInSearchMode || (isInFypMode ? fypFilters !== undefined : filters !== undefined)

  const isReady = primaryQueryPayloadReady && initialLoadComplete

  const feedState = {
    items: processedItems,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMoreItems,
    initialLoadComplete,
  }

  /** Primary column has nothing to show (ignores transient primary `loading` so near-match UI doesn’t flicker). Includes For You so city widen + “Other results from…” matches the main feed. */
  const primaryGridEmptyUi =
    !isInSearchMode &&
    processedItems.length === 0 &&
    hasActiveFilters &&
    initialLoadComplete &&
    !isProfilePage

  /** Mount masonry during initial fetch (0 items) so BaseMasonryGrid skeleton shows instead of a blank slot. */
  const primaryFeedAwaitingFirstPaint =
    !hideWhenEmpty &&
    itemsForGrid.length === 0 &&
    (isInSearchMode
      ? isSearching || loading || !initialLoadComplete
      : loading ||
        !initialLoadComplete ||
        !primaryQueryPayloadReady)

  const showPrimaryGrid =
    itemsForGrid.length > 0 ||
    primaryFeedAwaitingFirstPaint ||
    (!hideWhenEmpty && itemsForGrid.length === 0 && error != null)

  const showNearMatchSection =
    primaryGridEmptyUi && nearMatchExpansion != null && (nearMatchFeed.loading || nearItemsForGrid.length > 0)
  const showNearMatchIntro =
    primaryGridEmptyUi &&
    nearMatchExpansion != null &&
    (nearMatchFeed.loading || (nearMatchFeed.initialLoadComplete && nearItemsForGrid.length > 0))
  const showNearMatchHeading =
    nearMatchExpansion != null && nearMatchFeed.initialLoadComplete && nearItemsForGrid.length > 0
  /** Near-match hook is mounted with `filters: undefined` when idle; that leaves loading stuck true (see useQueryTattoos). Only gate empty UI on near-match loading when widened query actually runs (e.g. EE is a single-city market → no expansion → no secondary fetch). */
  const emptyStateWaitsOnNearMatch =
    shouldRunNearMatchQuery && nearMatchFeed.loading
  const showEmptyState =
    primaryGridEmptyUi &&
    !loading &&
    hasMeaningfulFilters &&
    hasStartedInitialLoadRef.current &&
    !hideWhenEmpty &&
    !emptyStateWaitsOnNearMatch &&
    (!nearMatchExpansion || (nearMatchFeed.initialLoadComplete && nearItemsForGrid.length === 0))

  /** When For You has no rows and we are not using near-match / shared filter empty copy (e.g. all global filters). */
  const showFypEmptyState =
    isInFypMode &&
    !isInSearchMode &&
    !isProfilePage &&
    fypFilters !== undefined &&
    itemsForGrid.length === 0 &&
    initialLoadComplete &&
    !loading &&
    !error &&
    hasStartedInitialLoadRef.current &&
    !hideWhenEmpty &&
    !showEmptyState &&
    !(primaryGridEmptyUi && emptyStateWaitsOnNearMatch) &&
    !(primaryGridEmptyUi && nearMatchExpansion != null && nearItemsForGrid.length > 0)

  const feedEmptyMessageClassName =
    'text-base font-medium text-muted-foreground leading-snug'

  return (
    <div>
      {showNearMatchIntro && (
        <div className="py-3 text-left">
          <p className={feedEmptyMessageClassName}>{t('feed.nearMatch.title')}</p>
        </div>
      )}
      {showNearMatchHeading && (
        <h3 className="mb-3 text-left text-base font-semibold text-foreground">
          {t('feed.nearMatch.otherResultsFrom', { country: nearMatchExpansion!.countryName })}
        </h3>
      )}

      {showEmptyState && (
        <div className="py-3 text-left">
          <p className={feedEmptyMessageClassName}>{t('feed.nearMatch.title')}</p>
        </div>
      )}

      {showFypEmptyState && (
        <div className="flex flex-col items-center justify-center py-12 text-center px-4">
          <div className="text-lg font-medium text-foreground mb-2">{t('feed.fyp.emptyTitle')}</div>
          <div className="text-sm text-muted-foreground max-w-sm">{t('feed.fyp.emptyDescription')}</div>
        </div>
      )}

      {showPrimaryGrid && (
        <BaseMasonryGrid
          key={masonryLayoutKey}
          items={itemsForGrid}
          loading={loading || !primaryQueryPayloadReady}
          loadingMore={feedState.loadingMore}
          hasMore={feedState.hasMore}
          error={feedState.error}
          loadMoreItems={feedState.loadMoreItems}
          renderItem={renderPrimaryCard}
          artists={artists}
          artistsLoading={artistsLoading}
          initialLoadComplete={isReady}
          editMode={editMode}
          onDelete={onDelete}
          debugSkeleton={debugSkeleton}
          skipInitialSkeleton={mountedWithCachedDataRef.current}
          scrollRootRef={scrollRootRef}
          compactUniformMasonryGap={isNestedContext}
          onSkeletonOverlayActiveChange={onPrimarySkeletonOverlayActiveChange}
        />
      )}

      {showNearMatchSection &&
        !(hideWhenEmpty && nearItemsForGrid.length === 0 && !nearMatchFeed.loading) && (
          <BaseMasonryGrid
            key={`grid-near-${masonryLayoutKey}`}
            items={nearItemsForGrid}
            loading={nearMatchFeed.loading || !nearMatchFilters}
            loadingMore={nearMatchFeed.loadingMore}
            hasMore={nearMatchFeed.hasMore}
            error={nearMatchFeed.error}
            loadMoreItems={nearMatchFeed.loadMoreItems}
            renderItem={renderNearMatchCard}
            artists={nearArtists}
            artistsLoading={nearArtistsLoading}
            initialLoadComplete={nearMatchFeed.initialLoadComplete}
            editMode={editMode}
            onDelete={onDelete}
            debugSkeleton={debugSkeleton}
            skipInitialSkeleton={false}
            scrollRootRef={scrollRootRef}
            compactUniformMasonryGap={isNestedContext}
          />
        )}
    </div>
  )
}
